import BaseElevationLayer from '@arcgis/core/layers/BaseElevationLayer';
import TileInfo from '@arcgis/core/layers/support/TileInfo';
import { SurfaceTin } from './SurfaceTin';

const BaseConstructor = (BaseElevationLayer as any).createSubclass({
  properties: {},

  tinEdges: [],
  totalDuration: 0,

  constructor: function () { },

  load: function () {
    const defaultTileInfo = TileInfo.create();
    const lods: any = [];

    const lmin = 13;
    const lmax = 18;
    // const lmin = 16;
    // const lmax = 16;
    for (let level = lmin; level <= lmax; level++) {
      lods.push(defaultTileInfo.lods[level]);
    }

    const tileInfo = new TileInfo({
      dpi: defaultTileInfo.dpi,
      format: defaultTileInfo.format,
      lods: lods,
      origin: defaultTileInfo.origin,
      size: defaultTileInfo.size,
      spatialReference: defaultTileInfo.spatialReference,
    });

    this.tileInfo = tileInfo;
    this.spatialReference = this.tileInfo.spatialReference;

    const surfaceTin: SurfaceTin = this.surfaceTin;
    this.fullExtent = surfaceTin.computeExtent();

    // initialize the tinEdges collection
    this.tinEdges = [];
    for (const face of surfaceTin.faces) {
      const node1 = surfaceTin.nodes[face.nodeIndex1];
      const node2 = surfaceTin.nodes[face.nodeIndex2];
      const node3 = surfaceTin.nodes[face.nodeIndex3];

      if (node1 && node2 && node3) {
        addTinEdge.bind(this)([node1.x, node1.y, node1.z], [node2.x, node2.y, node2.z]);
        addTinEdge.bind(this)([node2.x, node2.y, node2.z], [node3.x, node3.y, node3.z]);
        addTinEdge.bind(this)([node3.x, node3.y, node3.z], [node1.x, node1.y, node1.z]);
      } else {
        console.warn('BAD TIN TRIANGLE:');
        console.warn(face);
        // throw new Error('Invalid TIN triangle with missing node(-s) detected');
      }
    }

    return this;
  },

  // Fetches the tile(s) visible in the view
  fetchTile: async function (level, row, col, options) {
    const startTime = Date.now();

    // TODO: implement a max cache size to prevent extraneous memory growth
    const cacheKey = level.toString() + '-' + row.toString() + '-' + col.toString();
    if (!this._resultcache) {
      this._resultcache = {};
    }
    if (this._resultcache[cacheKey]) {
      return this._resultcache[cacheKey];
    }

    const tileInfo: TileInfo = this.tileInfo;
    const lod = tileInfo.lods.find((lod) => {
      return lod.level === level;
    });
    if (!lod) {
      throw new Error('LOD not supported by this layer.');
    }

    const tileSizeX = tileInfo.size[0];
    const tileSizeY = tileInfo.size[1];
    // const noDataValue = options.noDataValue;
    const noDataValue = 3.4028234663852886e37;
    const maxZError = 0.1;
    const newData = {
      values: new Float32Array((tileSizeX + 1) * (tileSizeY + 1)).fill(noDataValue),
      width: tileSizeX + 1,
      height: tileSizeY + 1,
      maxZError: maxZError,
      maxValue: noDataValue,
      minValue: noDataValue,
      noDataValue: noDataValue,
    };

    const originX = tileInfo.origin.x;
    const originY = tileInfo.origin.y;
    const resolution = lod.resolution;
    const mapTileWidth = tileSizeX * resolution;
    const mapTileHeight = tileSizeY * resolution;

    const tileXMin = originX + mapTileWidth * col;
    const tileYMax = originY - mapTileHeight * row;
    const tileXMax = tileXMin + mapTileWidth;
    const tileYMin = tileYMax - mapTileHeight;

    let minZ: number | undefined;
    let maxZ: number | undefined;

    // compute map coordinate Y for each elevation pixel row
    for (let iy = 0; iy <= tileSizeY; iy++) {
      const dataIndexOffset = iy * (tileSizeX + 1);
      const pixelY = tileYMax - iy * lod.resolution;
      let needAllDefaultRow = true;

      const intersectionPoints: number[][] = getIntersectionPoints.bind(this)(pixelY);
      // need at least 2 intersection points to interpolate Z
      if (intersectionPoints.length > 1) {
        const lastIntersectIndex = intersectionPoints.length - 1;
        // check if this pixel row overlats the intersection list in X
        if (tileXMin <= intersectionPoints[lastIntersectIndex][0] && tileXMax >= intersectionPoints[0][0]) {
          needAllDefaultRow = false;

          let firstXIndex = Math.ceil((intersectionPoints[0][0] - tileXMin) / lod.resolution);
          if (0 > firstXIndex) {
            firstXIndex = 0;
          }

          // NOTE: commented out because no need to return data for area not within the SurfaceTin
          // for (let ix = 0; ix < firstXIndex; ix++) {
          //   const dataIndex = dataIndexOffset + ix;
          //   if (data.values[dataIndex] !== data.noDataValue) {
          //     // multiply the elevation value by the exaggeration value
          //     data.values[dataIndex] *= this.exaggeration;
          //   }
          // }

          let firstXIndexPast =
            tileSizeX - Math.ceil((tileXMax - intersectionPoints[lastIntersectIndex][0]) / lod.resolution) + 1;
          if (tileSizeX + 1 < firstXIndexPast) {
            firstXIndexPast = tileSizeX + 1;
          }

          // NOTE: commented out because no need to return data for area not within the SurfaceTin
          // for (let ix = firstXIndexPast; ix <= tileSizeX; ix++) {
          //   const dataIndex = dataIndexOffset + ix;
          //   if (data.values[dataIndex] !== data.noDataValue) {
          //     // multiply the elevation value by the exaggeration value
          //     data.values[dataIndex] *= this.exaggeration;
          //   }
          // }

          let currentIntersectIndex = 0;
          for (let ix = firstXIndex; ix < firstXIndexPast; ix++) {
            const dataIndex = dataIndexOffset + ix;
            const pixelX = tileXMin + ix * lod.resolution;
            while (
              currentIntersectIndex < lastIntersectIndex &&
              intersectionPoints[currentIntersectIndex + 1][0] <= pixelX
            ) {
              currentIntersectIndex++;
            }
            let z: number = 0;
            if (intersectionPoints[currentIntersectIndex][0] === pixelX) {
              z = intersectionPoints[currentIntersectIndex][1];
            } else {
              //interpolate Z
              z =
                ((intersectionPoints[currentIntersectIndex + 1][1] - intersectionPoints[currentIntersectIndex][1]) *
                  (pixelX - intersectionPoints[currentIntersectIndex][0])) /
                (intersectionPoints[currentIntersectIndex + 1][0] - intersectionPoints[currentIntersectIndex][0]) +
                intersectionPoints[currentIntersectIndex][1];
            }
            // data.values[dataIndex] = z * this.exaggeration;
            newData.values[dataIndex] = z;

            //the array storage type is Float32, so read z back to see value after type coersion is applied
            z = newData.values[dataIndex];

            // update the minZ and maxZ
            if (minZ === undefined) {
              minZ = z;
              maxZ = z;
            } else {
              minZ = Math.min(minZ!, z);
              maxZ = Math.max(maxZ!, z);
            }
          }
        }
      }

      // NOTE: commented out because I don't think this is needed to get the desired result
      // if (needAllDefaultRow) {
      //   for (let ix = 0; ix <= tileSizeX; ix++) {
      //     const dataIndex = dataIndexOffset + ix;
      //     if (data.values[dataIndex] !== data.noDataValue) {
      //       // multiply the elevation value by the exaggeration value
      //       data.values[dataIndex] *= this.exaggeration;
      //     }
      //   }
      // }
    }

    newData.minValue = minZ!;
    newData.maxValue = maxZ!;

    // this._resultcache[cacheKey] = newData;

    const endTime = Date.now();
    const duration = endTime - startTime;
    // console.log('durationG', level, row, col, duration);

    this.totalDuration += duration;
    // console.log('totalDurationG', this.totalDuration);

    // console.log('cacheKey', cacheKey);
    // if (cacheKey === '16-24501-15115') {
    //   const json = JSON.stringify(newData);
    //   console.log('JSON G:', json);
    // }

    return newData;
  },
}) as __esri.ElevationLayerConstructor;

function addTinEdge(this: any, p1: number[], p2: number[]): void {
  let topPoint = p1;
  let bottomPoint = p2;

  if (topPoint[1] < bottomPoint[1] || (topPoint[1] === bottomPoint[1] && topPoint[0] < bottomPoint[0])) {
    topPoint = p2;
    bottomPoint = p1;
  }

  const duplicate = this.tinEdges.find((item: number[][]) => {
    return (
      item[0][0] === topPoint[0] &&
      item[0][1] === topPoint[1] &&
      item[1][0] === bottomPoint[0] &&
      item[1][1] === bottomPoint[1]
    );
  });

  if (!duplicate) {
    this.tinEdges.push([topPoint, bottomPoint]);
  }
}

function getIntersectionPoints(this: any, y: number): number[][] {
  const result: number[][] = [];

  this.tinEdges.forEach((item: number[][]) => {
    if (item[0][1] > y) {
      if (item[1][1] < y) {
        const ratio = (y - item[1][1]) / (item[0][1] - item[1][1]);
        insertSorted(result, [
          (item[0][0] - item[1][0]) * ratio + item[1][0],
          (item[0][2] - item[1][2]) * ratio + item[1][2],
        ]);
      } else if (item[1][1] === y) {
        insertSorted(result, [item[1][0], item[1][2]]);
      }
    } else if (item[0][1] === y) {
      insertSorted(result, [item[0][0], item[0][2]]);
      if (item[1][1] === y) {
        insertSorted(result, [item[1][0], item[1][2]]);
      }
    }
  });

  return result;
}

function insertSorted(list: number[][], intersection: number[]): void {
  if (0 === list.length) {
    list.push(intersection);
  } else if (intersection[0] < list[0][0]) {
    list.splice(0, 0, intersection);
  } else {
    let top = list.length - 1;
    if (intersection[0] > list[top][0]) {
      list.push(intersection);
    } else {
      let bot = 0;
      let mid = Math.floor(top / 2);
      while (true) {
        if (intersection[0] === list[mid][0] || intersection[0] === list[top][0] || intersection[0] === list[bot][0]) {
          break;
        }
        if (top === bot + 1) {
          list.splice(top, 0, intersection);
          break;
        }
        if (intersection[0] > list[mid][0]) {
          bot = mid;
        } else {
          // if (intersection[0] < list[mid][0])
          top = mid;
        }
        mid = Math.floor((top + bot) / 2);
      }
    }
  }
}

export class GatchikElevationLayer extends BaseConstructor {
  readonly surfaceTin: SurfaceTin;

  constructor(surfaceTin: SurfaceTin) {
    super();
    this.surfaceTin = surfaceTin;
  }
}
